import { useTranslation, t } from '../../i18n';
import React from 'react';
import { Select, Input, Button, Segmented, Typography, Grid } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import TagsInput, { isIpOrCidr, isAsn, isDomainPattern } from '../TagsInput';
import { CountryLabel } from '../CountryFlag';

const { Text } = Typography;

// 运算符 → 值输入形态。list = 多值(回车添加),text = 单值,none = 无需值。
const OPS = {
  eq: { label: '等于', kind: 'text' },
  neq: { label: '不等于', kind: 'text' },
  in: { label: '是其中之一', kind: 'list' },
  not_in: { label: '不是其中任何一个', kind: 'list' },
  contain: { label: '包含', kind: 'text' },
  not_contain: { label: '不包含', kind: 'text' },
  starts_with: { label: '开头是', kind: 'text' },
  ends_with: { label: '结尾是', kind: 'text' },
  glob: { label: '通配匹配', kind: 'text' },
  not_glob: { label: '通配不匹配', kind: 'text' },
  rx: { label: '匹配正则', kind: 'text' },
  exists: { label: '存在', kind: 'none' },
  not_exists: { label: '不存在', kind: 'none' },
};

const COUNTRIES = [
  ['CN', '中国'], ['HK', '中国香港'], ['TW', '中国台湾'], ['MO', '中国澳门'], ['US', '美国'], ['JP', '日本'],
  ['KR', '韩国'], ['SG', '新加坡'], ['MY', '马来西亚'], ['TH', '泰国'], ['VN', '越南'], ['ID', '印度尼西亚'],
  ['PH', '菲律宾'], ['IN', '印度'], ['RU', '俄罗斯'], ['UA', '乌克兰'], ['DE', '德国'], ['FR', '法国'],
  ['GB', '英国'], ['NL', '荷兰'], ['IT', '意大利'], ['ES', '西班牙'], ['PL', '波兰'], ['SE', '瑞典'],
  ['CA', '加拿大'], ['BR', '巴西'], ['MX', '墨西哥'], ['AR', '阿根廷'], ['AU', '澳大利亚'], ['NZ', '新西兰'],
  ['TR', '土耳其'], ['IR', '伊朗'], ['SA', '沙特阿拉伯'], ['AE', '阿联酋'], ['IL', '以色列'], ['EG', '埃及'],
  ['ZA', '南非'], ['NG', '尼日利亚'], ['PK', '巴基斯坦'], ['BD', '孟加拉国'],
].map(([v, n]) => ({ value: v, label: `${n} ${v}` }));

// Select options for a field; COUNTRY options get a flag. `search` keeps type-to-filter working on plain text.
const valueOptions = (f, collection, t) => f.options?.map((o) => {
  const text = collection === 'COUNTRY' ? `${t(o.label.slice(0, -o.value.length - 1))} ${o.value}` : t(o.label);
  return { ...o, search: text, label: collection === 'COUNTRY' ? <CountryLabel code={o.value}>{text}</CountryLabel> : text };
});

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'].map((m) => ({ value: m, label: m }));
const EXTS = ['html', 'php', 'js', 'css', 'json', 'xml', 'jpg', 'png', 'gif', 'webp', 'svg', 'ico', 'mp4', 'zip', 'env', 'sql', 'bak']
  .map((e) => ({ value: e, label: e }));

// 字段:可用运算符(第一个为默认)、值候选、占位提示、是否需要名称(请求头/Cookie/参数名)。
export const FIELDS = {
  PATH: { label: '路径', group: '请求', ops: ['starts_with', 'glob', 'eq', 'contain', 'ends_with', 'in', 'rx', 'not_contain', 'not_glob', 'neq'], ph: '/admin' },
  HOST: { label: '主机名', group: '请求', ops: ['eq', 'glob', 'in', 'neq', 'not_in', 'ends_with', 'not_glob', 'rx'], ph: 'www.example.com' },
  METHOD: { label: '请求方法', group: '请求', ops: ['in', 'not_in'], options: METHODS },
  EXTENSION: { label: '文件扩展名', group: '请求', ops: ['in', 'not_in'], options: EXTS, ph: '不带点，如 php' },
  QS_ARGS: { label: '查询参数', group: '请求', named: '参数名', ops: ['eq', 'contain', 'rx', 'exists', 'not_exists', 'neq'], ph: '值' },
  HEADER: { label: '请求头', group: '请求', named: '请求头名', ops: ['eq', 'contain', 'rx', 'exists', 'not_exists', 'neq', 'not_contain'], ph: '值' },
  COOKIE: { label: 'Cookie', group: '请求', named: 'Cookie 名', ops: ['eq', 'exists', 'not_exists', 'contain', 'rx', 'neq'], ph: '值' },
  USER_AGENT: { label: 'User-Agent', group: '请求', ops: ['contain', 'not_contain', 'rx', 'eq', 'starts_with'], ph: 'curl' },
  REFERER: { label: 'Referer', group: '请求', ops: ['contain', 'not_contain', 'starts_with', 'eq', 'rx'], ph: 'https://example.com' },
  BODY: { label: '请求体', group: '请求', ops: ['contain', 'not_contain', 'rx'], ph: '值' },
  PROTOCOL: { label: '协议', group: '请求', ops: ['eq'], options: [{ value: 'https', label: 'HTTPS' }, { value: 'http', label: 'HTTP' }] },
  IP: { label: 'IP 地址', group: '来源', ops: ['in', 'not_in'], ph: '1.2.3.4 或 1.2.3.0/24，回车添加' },
  COUNTRY: { label: '国家/地区', group: '来源', ops: ['in', 'not_in'], options: COUNTRIES },
  ASN: { label: 'ASN', group: '来源', ops: ['in', 'not_in'], ph: '如 13335，回车添加' },
  JA3: { label: 'JA3 指纹', group: '指纹', ops: ['in', 'not_in'], ph: '指纹哈希，回车添加' },
  JA4: { label: 'JA4 指纹', group: '指纹', ops: ['in', 'not_in'], ph: '指纹，回车添加' },
  H2: { label: 'HTTP/2 指纹', group: '指纹', ops: ['in', 'not_in'], ph: '指纹，回车添加' },
};

// 旧数据兼容:URI 视同路径;键名可能在 key / collection_var。
const normField = (c) => (c === 'URI' ? 'PATH' : c);

export function normalizeConditions(expression) {
  const list = Array.isArray(expression?.conditions) ? expression.conditions : [];
  return {
    op: expression?.op === 'or' ? 'or' : 'and',
    conditions: list.map((c) => ({
      collection: normField(c.collection),
      name: c.collection_var ?? c.key ?? '',
      op: c.op || c.operator,
      var: c.var,
    })),
  };
}

/** 编辑器内部值 → 下发格式 {op, conditions:[{collection, op, var, collection_var?}]} */
export function toExpression(value) {
  return {
    op: value?.op === 'or' ? 'or' : 'and',
    conditions: (value?.conditions || []).map((c) => {
      const kind = OPS[c.op]?.kind;
      let v = c.var;
      if (kind === 'list') v = (Array.isArray(v) ? v : String(v ?? '').split(/[\n,]/)).map((x) => String(x).trim()).filter(Boolean);
      else if (kind === 'none') v = '';
      else v = Array.isArray(v) ? v.join(',') : String(v ?? '');
      if (c.collection === 'COUNTRY' && Array.isArray(v)) v = v.map((x) => x.toUpperCase());
      if (c.collection === 'EXTENSION' && Array.isArray(v)) v = v.map((x) => x.replace(/^\./, '').toLowerCase());
      const out = { collection: c.collection, op: c.op, var: v };
      if (FIELDS[c.collection]?.named) out.collection_var = (c.name || '').trim();
      return out;
    }),
  };
}

/** 校验:返回第一条错误文案,无错返回 null */
export function validateConditions(value) {
  const list = value?.conditions || [];
  if (!list.length) return t('Add at least one condition');
  for (let i = 0; i < list.length; i++) {
    const c = list[i];
    const f = FIELDS[c.collection];
    if (!f) return t('Condition {{value}}: please select a field', { value: i + 1 });
    if (f.named && !String(c.name || '').trim()) return t('Condition {{value}}: please enter {{named}}', { value: i + 1, named: t(f.named) });
    const kind = OPS[c.op]?.kind;
    const empty = kind === 'list'
      ? !(Array.isArray(c.var) ? c.var.length : String(c.var || '').trim())
      : kind === 'text' && !String(c.var ?? '').trim();
    if (empty) return t('Condition {{value}}: please enter a value', { value: i + 1 });
  }
  return null;
}

const fieldOptions = Object.entries(
  Object.entries(FIELDS).reduce((acc, [k, f]) => {
    (acc[f.group] = acc[f.group] || []).push({ value: k, label: f.label });
    return acc;
  }, {}),
).map(([group, options]) => ({ label: group, options }));

const listError = (collection, v, t) => {
  if (collection === 'IP') return isIpOrCidr(v) ? null : t('Invalid IP / CIDR');
  if (collection === 'ASN') return isAsn(v) ? null : t('ASN must be a number');
  if (collection === 'HOST') return isDomainPattern(v) ? null : t('Invalid hostname');
  if (collection === 'PATH') return v.startsWith('/') ? null : t('Paths start with /');
  return null;
};

function ConditionRow({ cond, onChange, onRemove, canRemove, stacked }) {
  const { t } = useTranslation();
  const f = FIELDS[cond.collection] || FIELDS.PATH;
  const kind = OPS[cond.op]?.kind || 'text';

  const changeField = (collection) => {
    const nf = FIELDS[collection];
    onChange({ collection, name: '', op: nf.ops[0], var: OPS[nf.ops[0]].kind === 'list' ? [] : '' });
  };
  const changeOp = (op) => {
    const k = OPS[op].kind;
    let v = cond.var;
    if (k === 'list') v = Array.isArray(v) ? v : (v ? [String(v)] : []);
    else if (k === 'text') v = Array.isArray(v) ? (v[0] || '') : (v ?? '');
    else v = '';
    onChange({ ...cond, op, var: v });
  };

  let valueInput = null;
  if (kind === 'list' && !f.options) {
    valueInput = (
      <TagsInput
        format="array"
        value={Array.isArray(cond.var) ? cond.var : []}
        onChange={(v) => onChange({ ...cond, var: v })}
        placeholder={t(f.ph)}
        lowercase={cond.collection === 'HOST'}
        commaSeparates={cond.collection !== 'H2'}
        validate={(x) => listError(cond.collection, x, t)}
        maxTagCount="responsive"
      />
    );
  } else if (kind === 'list') {
    valueInput = (
      <Select
        mode={f.options && cond.collection !== 'EXTENSION' ? 'multiple' : 'tags'}
        options={valueOptions(f, cond.collection, t)}
        value={Array.isArray(cond.var) ? cond.var : []}
        onChange={(v) => onChange({ ...cond, var: v })}
        placeholder={f.options ? t('Select') : t(f.ph)}
        tokenSeparators={[',', ' ', '\n']}
        showSearch
        optionFilterProp="search"
        style={{ width: '100%' }}
        maxTagCount="responsive"
      />
    );
  } else if (kind === 'text') {
    valueInput = f.options ? (
      <Select options={valueOptions(f, cond.collection, t)} value={cond.var || undefined} onChange={(v) => onChange({ ...cond, var: v })} placeholder={t('Select')} style={{ width: '100%' }} />
    ) : (
      <Input
        value={cond.var}
        onChange={(e) => onChange({ ...cond, var: e.target.value })}
        placeholder={cond.op === 'rx' ? t('Regex, e.g., ^/api/v\\d+/') : (cond.op === 'glob' || cond.op === 'not_glob') ? (cond.collection === 'HOST' ? '*.example.com' : '/api/*') : t(f.ph)}
      />
    );
  }

  const cell = (w, el) => (
    <div style={stacked ? { flex: '1 1 100%', minWidth: 0 } : { width: w === 'auto' ? undefined : w, flex: w === 'auto' ? 1 : 'none', minWidth: 0 }}>{el}</div>
  );

  return (
    <div style={{ display: 'flex', flexWrap: stacked ? 'wrap' : 'nowrap', gap: 8, alignItems: 'center', padding: '8px 0' }}>
      {cell(140, <Select options={fieldOptions.map(g => ({ ...g, label: t(g.label), options: g.options.map(o => ({ ...o, label: t(o.label) })) }))} value={cond.collection} onChange={changeField} style={{ width: '100%' }} />)}
      {f.named && cell(120, <Input value={cond.name} onChange={(e) => onChange({ ...cond, name: e.target.value })} placeholder={t(f.named)} />)}
      {cell(150, (
        <Select
          value={cond.op}
          onChange={changeOp}
          options={f.ops.map((o) => ({ value: o, label: t(OPS[o].label) }))}
          style={{ width: '100%' }}
        />
      ))}
      {kind !== 'none' && cell('auto', valueInput)}
      {kind === 'none' && !stacked && <div style={{ flex: 1 }} />}
      <Button type="text" danger icon={<DeleteOutlined />} onClick={onRemove} disabled={!canRemove} />
    </div>
  );
}

/**
 * 规则条件编辑器(受控):value = {op:'and'|'or', conditions:[{collection, name, op, var}]}。
 * 每个字段只给出适用的运算符,值输入随运算符/字段切换(多值标签、国家/方法下拉、正则、无需值)。
 */
export default function RuleConditionEditor({ value, onChange, onEdit }) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const stacked = !screens.md;
  const v = value || { op: 'and', conditions: [] };
  const conds = v.conditions || [];
  const set = (patch) => {
    onEdit?.();
    onChange?.({ ...v, ...patch });
  };
  const update = (i, c) => set({ conditions: conds.map((x, j) => (j === i ? c : x)) });

  return (
    <div>
      {conds.length > 1 && (
        <div style={{ marginBottom: 4 }}>
          <Segmented
            size="small"
            value={v.op}
            onChange={(op) => set({ op })}
            options={[{ value: 'and', label: t('Match all conditions') }, { value: 'or', label: t('Match any condition') }]}
          />
        </div>
      )}
      {conds.map((c, i) => (
        <div key={i} style={{ borderTop: i ? '1px dashed var(--ant-color-border-secondary, #f0f0f0)' : 'none' }}>
          {i > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{v.op === 'or' ? t('Or') : t('AND')}</Text>}
          <ConditionRow
            cond={c}
            stacked={stacked}
            canRemove={conds.length > 1}
            onChange={(nc) => update(i, nc)}
            onRemove={() => set({ conditions: conds.filter((_, j) => j !== i) })}
          />
        </div>
      ))}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={() => set({ conditions: [...conds, { collection: 'PATH', name: '', op: 'starts_with', var: '' }] })}
        style={{ marginTop: 4 }}
      >
        {t('Add condition')}
      </Button>
    </div>
  );
}
